<template>
  <div class="actualities-page container">
    <TheBreadcrumb :pages="arianeRoute" />
    <HeaderPage :page="currentPageInfo" />
    <div class="order">
      <section
        v-if="metaContent.value?.description"
        class="section order-last"
      >
        <div
          class="description"
          v-html="metaContent.value?.description"
        />
      </section>
      <div class="order-first">
        <section
          v-if="userActualities.length && authenticated"
          class="my-actualities section"
        >
          <div class="title-line">
            <h2 class="title-strat">
              Dernières actualités sur vos communautés
            </h2>
          </div>
          <FilterMain
            :current="filterGame"
            :items="uniqueCommunities"
            :action="(slug) => (filterGame = slug)"
          />
          <div class="slider">
            <CardSlider :cards="myCardsFiltered" />
          </div>
        </section>
        <SearchResults
          :is-loading="isLoading"
          placeholder-text="Rechercher une actualité"
          @search-update="searchUpdateHandler"
        >
          <template #title>
            <h2 class="title-strat">
              {{
                authenticated && !!userActualities.length
                  ? "Plus d'actualités des communautés"
                  : 'Toutes les actualités des communautés'
              }}
            </h2>
          </template>
          <template #results>
            <CardContainer
              :items="actualities"
              :no-result-text="noContent"
            />
          </template>
        </SearchResults>
        <ThePagination
          :total-pages="actualitiesPages"
          :current-page="actualitiesCurrentPage"
          :go-to="changePageTo"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  middleware: async (to) => {
    const loadActus = useLoadActus()
    return loadActus(to)
  },
})

const actualitiesStore = useActualitiesStore()
const {
  actualities,
  userActualities,
  actualitiesPages,
  actualitiesCurrentPage,
  getPagesCount,
  getCurrentPage,
} = storeToRefs(actualitiesStore)

const link = useLinkPrevNext({
  currentPage: getCurrentPage,
  pagesCount: getPagesCount,
})

const arianeRoute = ref([
  {
    title: 'Toutes les actualités',
    route: '',
  },
])

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

useHead({
  title: 'Actus Jeux Vidéos de la communauté PlayStation',
  link,
  script: [breadcrumbSchema],
})

useSeoMeta({
  title: 'Actus Jeux Vidéos de la communauté PlayStation',
  description:
    'Soyez au courant des dernières infos sur les communautés du moment.',
  ogTitle: 'Toute l’actualité des communautés We Are PlayStation',
  ogDescription:
    'Soyez au courant des dernières infos sur les communautés du moment.',
  ogImage: 'https://www.weareplaystation.fr/img/toute-l-actualite.jpg',
})

const route = useRoute()

const metaContentStore = useMetaContentStore()

const filterGame = ref('all')

const currentPageInfo = {
  title: 'Toutes les actualités',
  image: '/img/toute-l-actualite.jpg',
  titleSmall: 'toutes les',
  titleBig: 'actualités',
  description:
    'Soyez au courant des dernières infos sur les communautés du moment.',
}

const searchData = async () => {
  isLoading.value = true

  await actualitiesStore.fetchActualities({
    page: 1,
    search: search.value,
  })

  if (!actualities.value.length) {
    noContent.value = search.value
      ? 'Aucun résultat pour votre recherche'
      : 'Aucun contenu'
  }

  isLoading.value = false
}

const { isLoading, search, searchUpdateHandler, noContent } = useSearching({
  searchData,
})

const metaContent = computed(() => {
  return metaContentStore.getTransverseBySlug(route.name)
})

const myCardsFiltered = computed(() => {
  if (filterGame.value === 'all') {
    return userActualities.value
  }
  return userActualities.value.filter(
    (card) => card.community.slug === filterGame.value,
  )
})

const communities = computed(() =>
  userActualities.value.map((card) => card.community),
)

const uniqueCommunities = uniqueBy(communities, 'slug')

const loadActus = useLoadActus()

const { authenticated } = useAuthenticated()

watch(authenticated, (newValue) => {
  if (newValue) {
    loadActus(route)
  }
})

const changePageTo = async (index = 1) => {
  if (!index) return

  await actualitiesStore.fetchActualities({
    page: index,
    search: search.value,
  })
}
</script>

<style lang="scss" scoped>
.actualities-page {
  min-height: 100vh;
  padding-bottom: 7rem;

  @media screen and (min-width: 700px) {
    padding-bottom: 10rem;
  }

  .my-actualities {
    @media screen and (max-width: 700px) {
      margin-top: 5rem;
      padding-right: 0;
      padding-left: 0;
    }
  }

  .title-line {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

.order {
  display: flex;
  flex-direction: column;

  &-first {
    order: 1;
  }

  &-last {
    order: 2;
    width: 100%;
  }

  .description {
    &:deep(h2) {
      margin-bottom: 1.5rem;
    }

    &:deep(h3) {
      margin-bottom: 1rem;
      font-family: SST;
      font-size: 1.5rem;
      font-weight: 500;
      line-height: 2rem;
      letter-spacing: 0.225rem;
      text-transform: uppercase;
    }

    &:deep(p) {
      margin-bottom: 1rem;
    }

    &:deep(ul) {
      margin-bottom: 1rem;
      padding-left: 1em;
      list-style: disc;
    }
  }
}
</style>
